import { queryOptions } from '@tanstack/react-query';
import fetchClient from '@eureca/recruiter-api-sdk/client';

/**
 * EU-641 — dados do painel "FAQs padrão" (proxies finos do BFF; o Core é o
 * dono do catálogo, do escopo e do gate de registro terminal). `fetchClient`
 * CRU com tipos locais — mesmo precedente dos catálogos do EU-593
 * (candidate-filter-catalogs.ts); regen do SDK atualiza o contrato depois.
 */

export interface DefaultFaqItem {
  id: number;
  question: string;
  answer: string;
  position: number;
  hidden: boolean;
}

export interface DefaultFaqCatalogItem {
  id: number;
  key: string;
  question: string;
  answer: string;
  position: number;
}

export class DefaultFaqsFetchError extends Error {
  readonly name = 'DefaultFaqsFetchError';
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message);
  }
}

async function fetchJson<T>(url: string, signal?: AbortSignal): Promise<T> {
  const res = await fetchClient<T>({ method: 'GET', url, signal });
  if (res.error) throw new DefaultFaqsFetchError(`Failed to load ${url}`, res.error.status);
  return res.data;
}

export type DefaultFaqsQueryContext =
  | { kind: 'job'; jobId: string }
  | { kind: 'program'; programId: string };

/** Chave/rota unificadas por contexto — um único shape de options (o union de
 *  dois `queryOptions` distintos quebra a inferência do `useQuery`). */
export function defaultFaqsQueryOptions(context: DefaultFaqsQueryContext, enabled = true) {
  const id = context.kind === 'job' ? context.jobId : context.programId;
  const url =
    context.kind === 'job'
      ? `/jobs/${context.jobId}/default-faqs`
      : `/programs/${context.programId}/default-faqs`;
  return queryOptions({
    queryKey: ['recruiter', 'default-faqs', context.kind, id] as const,
    queryFn: ({ signal }) => fetchJson<{ items: DefaultFaqItem[] }>(url, signal),
    staleTime: 30_000,
    enabled,
  });
}

/** Catálogo puro (kind=program) — CRIAÇÃO de programa (registro ainda não existe). */
export function programDefaultFaqsCatalogQueryOptions() {
  return queryOptions({
    queryKey: ['recruiter', 'programs', 'default-faqs-catalog'] as const,
    queryFn: ({ signal }) =>
      fetchJson<{ items: DefaultFaqCatalogItem[] }>('/programs/default-faqs-catalog', signal),
    staleTime: 5 * 60_000,
  });
}

/**
 * Replace-set do conjunto OCULTO. O SPA manda o conjunto COMPLETO a cada
 * alternância (idempotente; FQC-012) — falha → o container reverte o switch
 * (FQC-013).
 */
export async function putHiddenDefaultFaqs(
  context: { kind: 'job'; jobId: string } | { kind: 'program'; programId: string },
  faqIds: number[]
): Promise<void> {
  const url =
    context.kind === 'job'
      ? `/jobs/${context.jobId}/hidden-default-faqs`
      : `/programs/${context.programId}/hidden-default-faqs`;
  const res = await fetchClient<{ hiddenFaqIds: number[] }, unknown, { faqIds: number[] }>({
    method: 'PUT',
    url,
    data: { faqIds },
  });
  if (res.error) throw new DefaultFaqsFetchError(`Failed to persist ${url}`, res.error.status);
}
